// Loud-notification helpers for the agent chat dock: a short audio ping, a browser
// (desktop) notification, and a tab-title flash. The dock's passive badge is invisible
// when the agent is on another browser tab or app — these make a new/transferred chat
// noticeable anywhere. Everything here is best-effort: each API can be absent (jsdom,
// old browsers) or denied by the user, and failure must never break the dock.

// At most one audible ping per window — a chatty shopper must not turn the dock into a slot machine.
const PING_THROTTLE_MS = 3000;
let lastPingAt = 0;
let audioCtx = null;

export function playPing(now = Date.now()) {
  if (now - lastPingAt < PING_THROTTLE_MS) return false;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return false;
    if (!audioCtx) audioCtx = new Ctx();
    // Autoplay policy can leave the context suspended until the user interacts; resume is
    // a no-op when already running and harmlessly rejected when still blocked.
    if (audioCtx.state === "suspended" && audioCtx.resume) audioCtx.resume().catch(() => {});
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.08, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 0.25);
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.start();
    osc.stop(audioCtx.currentTime + 0.25);
    lastPingAt = now;
    return true;
  } catch (e) {
    return false;
  }
}

// Ask for desktop-notification permission once, lazily. Never re-prompts after a denial.
export function ensureNotifyPermission() {
  try {
    if (window.Notification && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  } catch (e) {
    /* noop */
  }
}

export function notifyBrowser(title, body) {
  try {
    // A focused agent already sees the dock badge — desktop notifications are for
    // "I'm on another tab/app" only.
    if (document.hasFocus && document.hasFocus()) return null;
    if (!window.Notification || Notification.permission !== "granted") return null;
    const n = new Notification(title, { body: body || "", tag: "ecom-chat" });
    n.onclick = () => {
      try {
        window.focus();
        n.close();
      } catch (e) {
        /* noop */
      }
    };
    setTimeout(() => {
      try {
        n.close();
      } catch (e) {
        /* noop */
      }
    }, 6000);
    return n;
  } catch (e) {
    return null;
  }
}

// --- tab-title flash: alternate "(n) 💬 New chat message" with the real title until the
// tab regains focus. The base title is captured once so nested flashes don't stack.
let flashTimer = null;
let baseTitle = null;

export function flashTitle(text) {
  try {
    if (document.hasFocus && document.hasFocus()) return;
    if (baseTitle === null) baseTitle = document.title;
    if (flashTimer) clearInterval(flashTimer);
    let showAlt = true;
    document.title = text;
    flashTimer = setInterval(() => {
      showAlt = !showAlt;
      document.title = showAlt ? text : baseTitle;
    }, 1200);
    window.addEventListener("focus", stopTitleFlash, { once: true });
  } catch (e) {
    /* noop */
  }
}

export function stopTitleFlash() {
  try {
    if (flashTimer) {
      clearInterval(flashTimer);
      flashTimer = null;
    }
    if (baseTitle !== null) {
      document.title = baseTitle;
      baseTitle = null;
    }
  } catch (e) {
    /* noop */
  }
}
